<script setup>
import { ref, computed, watch, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { Carousel, Slide } from 'vue3-carousel';
import 'vue3-carousel/dist/carousel.css';
import LandingThreatsContent from './LandingThreatsContent.vue';
import LandingCtaButton from './LandingCtaButton.vue';
import { useScrollAnimations } from '@/composables/useScrollAnimations';
import phishingImg from '@/assets/phishing.jpg';
import ceoFraudImg from '@/assets/ceoFraud.jpg';
import ransomwareImg from '@/assets/ransomware.png';
import secureTravelImg from '@/assets/secureTravel.jpeg';
import socialEngineeringImg from '@/assets/socialEngineering.jpeg';
import dataProtectionImg from '@/assets/dataProtection.jpeg';
import messengersImg from '@/assets/messenger.jpg';


const { t } = useI18n();

const threats = computed(() => [
    {
        id: 1,
        title: t('landingPage.v2.threats.items[0].title'),
        example: t('landingPage.v2.threats.items[0].example'),
        reality: t('landingPage.v2.threats.items[0].reality'),
        liability: t('landingPage.v2.threats.items[0].liability'),
        image: ceoFraudImg
    },
    {
        id: 2,
        title: t('landingPage.v2.threats.items[1].title'),
        example: t('landingPage.v2.threats.items[1].example'),
        reality: t('landingPage.v2.threats.items[1].reality'),
        liability: t('landingPage.v2.threats.items[1].liability'),
        image: ransomwareImg
    },
    {
        id: 3,
        title: t('landingPage.v2.threats.items[2].title'),
        example: t('landingPage.v2.threats.items[2].example'),
        reality: t('landingPage.v2.threats.items[2].reality'),
        liability: t('landingPage.v2.threats.items[2].liability'),
        image: phishingImg
    },
    {
        id: 4,
        title: t('landingPage.v2.threats.items[3].title'),
        example: t('landingPage.v2.threats.items[3].example'),
        reality: t('landingPage.v2.threats.items[3].reality'),
        liability: t('landingPage.v2.threats.items[3].liability'),
        image: socialEngineeringImg
    },
    {
        id: 5,
        title: t('landingPage.v2.threats.items[4].title'),
        example: t('landingPage.v2.threats.items[4].example'),
        reality: t('landingPage.v2.threats.items[4].reality'),
        liability: t('landingPage.v2.threats.items[4].liability'),
        image: messengersImg
    },
    {
        id: 6,
        title: t('landingPage.v2.threats.items[5].title'),
        example: t('landingPage.v2.threats.items[5].example'),
        reality: t('landingPage.v2.threats.items[5].reality'),
        liability: t('landingPage.v2.threats.items[5].liability'),
        image: null
    },
    {
        id: 7,
        title: t('landingPage.v2.threats.items[6].title'),
        example: t('landingPage.v2.threats.items[6].example'),
        reality: t('landingPage.v2.threats.items[6].reality'),
        liability: t('landingPage.v2.threats.items[6].liability'),
        image: null
    },
    {
        id: 8,
        title: t('landingPage.v2.threats.items[7].title'),
        example: t('landingPage.v2.threats.items[7].example'),
        reality: t('landingPage.v2.threats.items[7].reality'),
        liability: t('landingPage.v2.threats.items[7].liability'),
        image: dataProtectionImg
    },
    {
        id: 9,
        title: t('landingPage.v2.threats.items[8].title'),
        example: t('landingPage.v2.threats.items[8].example'),
        reality: t('landingPage.v2.threats.items[8].reality'),
        liability: t('landingPage.v2.threats.items[8].liability'),
        image: null
    },
    {
        id: 10,
        title: t('landingPage.v2.threats.items[9].title'),
        example: t('landingPage.v2.threats.items[9].example'),
        reality: t('landingPage.v2.threats.items[9].reality'),
        liability: t('landingPage.v2.threats.items[9].liability'),
        image: secureTravelImg
    }
]);

const activeIndex = ref(0);
const carouselRef = ref(null);
const scrollContainer = ref(null);
const sectionRef = ref(null);
const isPaused = ref(false);

const { animateInView, slideUp } = useScrollAnimations();

// Sync tab scroll when activeIndex changes (driven by Carousel or clicks)
watch(activeIndex, (newIndex) => {
    scrollToTab(newIndex);
});

const setActive = (index) => {
    activeIndex.value = index;
    // Carousel v-model will handle the slide change
};

const nextSlide = () => {
    carouselRef.value?.next();
};

const prevSlide = () => {
    carouselRef.value?.prev();
};

const scrollToTab = (index) => {
    if (scrollContainer.value && scrollContainer.value.firstElementChild) {
        const tab = scrollContainer.value.firstElementChild.children[index];
        if (tab) {
            const container = scrollContainer.value;
            const scrollLeft = tab.offsetLeft - (container.clientWidth / 2) + (tab.clientWidth / 2);
            container.scrollTo({
                left: scrollLeft,
                behavior: 'smooth'
            });
        }
    }
};

onMounted(() => {
    if (sectionRef.value) {
        animateInView(sectionRef.value, () => {
            slideUp(sectionRef.value);
        });
    }
});
</script>

<template>
    <div class="py-24 opacity-0" ref="sectionRef" @mouseenter="isPaused = true" @mouseleave="isPaused = false">
        <div class="mx-auto max-w-7xl px-6 lg:px-8">
            <!-- Header -->
            <div class="text-center mb-16 max-w-7xl mx-auto">
                <h2 class="text-3xl font-bold tracking-tight text-white sm:text-5xl mb-6"
                    v-html="$t('landingPage.v2.threats.header.title')">
                </h2>
                <p class="text-lg text-gray-400 leading-relaxed">
                    {{ $t('landingPage.v2.threats.header.subtitle') }}
                </p>
            </div>

            <!-- Navigation Tabs -->
            <div class="relative flex items-center mb-12">
                <button @click="prevSlide" aria-label="Previous slide"
                    class="p-2 text-gray-400 hover:text-white transition-colors hidden sm:block">
                    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5"
                        stroke="#2CA58D" class="w-8 h-8">
                        <path stroke-linecap="round" stroke-linejoin="round" d="M15.75 19.5L8.25 12l7.5-7.5" />
                    </svg>
                </button>

                <div class="flex-1 overflow-x-auto no-scrollbar mx-4" ref="scrollContainer">
                    <div class="flex space-x-2 sm:space-x-4 min-w-max border-b border-gray-700 pb-1">
                        <button v-for="(threat, index) in threats" :key="threat.id" @click="setActive(index)"
                            :aria-label="'Show ' + threat.title + ' slide'"
                            class="px-4 py-3 text-lg font-medium transition-all duration-300 relative whitespace-nowrap"
                            :class="activeIndex === index ? 'text-white' : 'text-gray-500 hover:text-gray-300'">
                            {{ threat.title }}
                            <span v-if="activeIndex === index"
                                class="absolute bottom-[-5px] left-0 w-full h-1 bg-gradient-to-r from-terciary to-teal-500 rounded-full"></span>
                        </button>
                    </div>
                </div>

                <button @click="nextSlide" aria-label="Next slide"
                    class="p-2 text-gray-400 hover:text-white transition-colors hidden sm:block">
                    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5"
                        stroke="#2CA58D" class="w-8 h-8">
                        <path stroke-linecap="round" stroke-linejoin="round" d="M8.25 4.5l7.5 7.5-7.5 7.5" />
                    </svg>
                </button>
            </div>

            <!-- Carousel Content Area -->
            <div class="relative min-h-[500px]">
                <Carousel ref="carouselRef" v-model="activeIndex" :items-to-show="1" :wrap-around="true"
                    :autoplay="isPaused ? 0 : 5000" :transition="500">
                    <Slide v-for="threat in threats" :key="threat.id">
                        <div class="w-full h-full px-1">
                            <LandingThreatsContent :threat="threat" />
                        </div>
                    </Slide>
                </Carousel>
            </div>

            <!-- Pagination Dots -->
            <div class="flex justify-center mt-10 space-x-4">
                <button v-for="(threat, index) in threats" :key="`dot-${threat.id}`" @click="setActive(index)"
                    :aria-label="'Go to slide ' + (index + 1)" class="w-4 h-4 rounded-full transition-all duration-300"
                    :class="activeIndex === index ? 'bg-pink-500 w-4' : 'bg-gray-600 hover:bg-gray-500'"></button>
            </div>

            <!-- CTA -->
            <div class="mt-16 flex items-center justify-center">
                <LandingCtaButton sectionName="Threats" :text="$t('landingPage.v2.cta.threats')" />
            </div>
        </div>
    </div>
</template>

<style scoped>
.no-scrollbar::-webkit-scrollbar {
    display: none;
}

.no-scrollbar {
    -ms-overflow-style: none;
    scrollbar-width: none;
}
</style>
